import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { Query, useQuery, useQueryClient } from "@tanstack/react-query";
import { TroubleshootingService } from "../services/troubleshooting.service";
import {
  isForbiddenError,
  LIVE_INTERVAL_MS,
  liveBackoffMs,
  PARTICIPANT_TIMELINE_SETTING,
  ParticipantTimelineConfig,
  parseParticipantTimelineSetting,
} from "../utils/troubleshooting.utils";
import { useGetSettingByName } from "./useSetting";
import { TimelineQueryParams } from "../domain/troubleshooting.interface";

/** Minimum characters before the participant search hits the API. */
export const TROUBLESHOOTING_SEARCH_MIN_CHARS = 2;

/** Every troubleshooting query key starts with this prefix. */
export const TROUBLESHOOTING_QUERY_PREFIX = "coyn-troubleshooting-";

/** React Query's own default when the client sets no `retry`. */
const DEFAULT_RETRIES = 3;

export const troubleshootingKeys = {
  search: (q: string) => ["coyn-troubleshooting-search", q] as const,
  timeline: (participantId: string, params: TimelineQueryParams) =>
    [
      "coyn-troubleshooting-timeline",
      participantId,
      params.from,
      params.to,
      (params.lanes || []).join(","),
      !!params.failuresOnly,
    ] as const,
  event: (participantId: string, eventId: string) =>
    ["coyn-troubleshooting-event", participantId, eventId] as const,
  journeys: (participantId: string, from: string, to: string) =>
    ["coyn-troubleshooting-journeys", participantId, from, to] as const,
  sessionStatus: (participantId: string) =>
    ["coyn-troubleshooting-session-status", participantId] as const,
};

type RetryOption =
  | boolean
  | number
  | ((failureCount: number, error: unknown) => boolean)
  | undefined;

/**
 * Contract §15: never retry a 403 (missing PART_TIMELINE_VIEW on the
 * backend); everything else follows the client's default retry policy.
 */
export const retryUnlessForbidden =
  (defaultRetry: RetryOption) =>
  (failureCount: number, error: unknown): boolean => {
    if (isForbiddenError(error)) return false;
    if (typeof defaultRetry === "function") {
      return defaultRetry(failureCount, error);
    }
    if (typeof defaultRetry === "number") return failureCount < defaultRetry;
    if (typeof defaultRetry === "boolean") return defaultRetry;
    return failureCount < DEFAULT_RETRIES;
  };

const useRetryPolicy = () => {
  const queryClient = useQueryClient();
  return retryUnlessForbidden(
    queryClient.getDefaultOptions().queries?.retry as RetryOption,
  );
};

// Any troubleshooting query, whatever its data type.
type AnyQuery = Query<any, any, any, any>;

/** errorUpdateCount of each query at its last successful refresh. */
const liveFailureBaseline = new WeakMap<AnyQuery, number>();

/**
 * Live mode `refetchInterval`: every `baseMs` (the setting's
 * `liveRefreshSeconds`, 30 s by default); exponential back-off from that
 * base (up to 5 min) after failed refreshes; never again after a 403. React
 * Query only runs it while the tab is visible.
 */
export const liveRefetchInterval = (
  query: AnyQuery,
  baseMs: number = LIVE_INTERVAL_MS,
): number | false => {
  const { error, errorUpdatedAt, dataUpdatedAt, errorUpdateCount } =
    query.state;
  if (isForbiddenError(error)) return false;
  const lastRefreshFailed = !!error && errorUpdatedAt >= dataUpdatedAt;
  if (!lastRefreshFailed) {
    liveFailureBaseline.set(query, errorUpdateCount);
    return liveBackoffMs(0, baseMs);
  }
  return liveBackoffMs(
    errorUpdateCount - (liveFailureBaseline.get(query) ?? 0),
    baseMs,
  );
};

interface LiveOption {
  live?: boolean;
  /** Live refresh period (from the participantTimeline setting). */
  liveIntervalMs?: number;
}

const liveOptions = ({ live, liveIntervalMs }: LiveOption = {}) => ({
  refetchInterval: live
    ? (query: AnyQuery) => liveRefetchInterval(query, liveIntervalMs)
    : (false as const),
  refetchIntervalInBackground: false,
});

/**
 * Contract §16.1: tunable values of the screen from the `participantTimeline`
 * platform setting. Returns the defaults until (and unless) it loads, so the
 * screen never waits for it.
 */
export const useParticipantTimelineConfig = (): ParticipantTimelineConfig => {
  const { setting } = useGetSettingByName(PARTICIPANT_TIMELINE_SETTING);
  const raw = (setting as Record<string, unknown> | undefined)?.[
    PARTICIPANT_TIMELINE_SETTING
  ];
  return useMemo(() => parseParticipantTimelineSetting(raw), [raw]);
};

const isForbiddenTroubleshootingQuery = (query: Query) =>
  typeof query.queryKey[0] === "string" &&
  query.queryKey[0].startsWith(TROUBLESHOOTING_QUERY_PREFIX) &&
  isForbiddenError(query.state.error);

/**
 * True as soon as any troubleshooting call (search, timeline, detail,
 * journeys, session status) answered 403. The forbidden results are dropped
 * when the screen unmounts, so a later visit asks the backend again.
 */
export const useTroubleshootingForbidden = (): boolean => {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const subscribe = useCallback(
    (onChange: () => void) => cache.subscribe(onChange),
    [cache],
  );
  const getSnapshot = useCallback(
    () => cache.getAll().some(isForbiddenTroubleshootingQuery),
    [cache],
  );

  useEffect(
    () => () =>
      queryClient.removeQueries({ predicate: isForbiddenTroubleshootingQuery }),
    [queryClient],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
};

export const useTroubleshootingParticipantSearch = (q: string) => {
  const term = q.trim();
  const retry = useRetryPolicy();
  return useQuery({
    queryKey: troubleshootingKeys.search(term),
    queryFn: () => TroubleshootingService.searchParticipants(term),
    enabled: term.length >= TROUBLESHOOTING_SEARCH_MIN_CHARS,
    retry,
  });
};

export const useParticipantTimeline = (
  participantId: string,
  params: TimelineQueryParams,
  options?: { enabled?: boolean } & LiveOption,
) => {
  const retry = useRetryPolicy();
  return useQuery({
    ...liveOptions(options),
    queryKey: troubleshootingKeys.timeline(participantId, params),
    queryFn: () => TroubleshootingService.getTimeline(participantId, params),
    enabled:
      (options?.enabled ?? true) &&
      !!participantId &&
      !!params.from &&
      !!params.to,
    retry,
    // Keep the previous window on screen while a filter/zoom change loads —
    // but never another participant's events.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === participantId ? previousData : undefined,
  });
};

export const useTimelineEventDetail = (
  participantId: string,
  eventId: string | null | undefined,
) => {
  const retry = useRetryPolicy();
  return useQuery({
    queryKey: troubleshootingKeys.event(participantId, eventId || ""),
    queryFn: () =>
      TroubleshootingService.getTimelineEvent(participantId, eventId as string),
    enabled: !!participantId && !!eventId,
    retry,
  });
};

export const useValidationJourneys = (
  participantId: string,
  from: string,
  to: string,
  options?: { enabled?: boolean } & LiveOption,
) => {
  const retry = useRetryPolicy();
  return useQuery({
    ...liveOptions(options),
    queryKey: troubleshootingKeys.journeys(participantId, from, to),
    queryFn: () =>
      TroubleshootingService.getJourneys(participantId, { from, to }),
    enabled: (options?.enabled ?? true) && !!participantId && !!from && !!to,
    retry,
  });
};

export const useSessionStatus = (
  participantId: string,
  options?: LiveOption,
) => {
  const retry = useRetryPolicy();
  return useQuery({
    ...liveOptions(options),
    queryKey: troubleshootingKeys.sessionStatus(participantId),
    queryFn: () => TroubleshootingService.getSessionStatus(participantId),
    enabled: !!participantId,
    retry,
  });
};
